import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { cn } from '../lib/utils.js';
import { Input } from './Input.js';
import { Label } from './Label.js';
import { DatePopover } from './DatePopover.js';
import { DateTrigger } from './DateTrigger.js';
import { useDateFieldLabels, type DateFieldLabels } from './dateLabels.js';
import { focusRing } from '../lib/focus.js';
import {
  isTimeValue,
  nearestStepAtOrBelow,
  parseTime,
  quarterHourSteps,
  type TimeValue,
} from '../lib/timeValue.js';

/* ===========================================================================
 * A TIME — 24-hour `HH:MM`, and no meridiem toggle anywhere in it.
 *
 * See `lib/timeValue.ts` for why: every stored time in this product is 24-hour,
 * so a control offering AM/PM would be a second way to say a value that has
 * one, and would put the app's one genuinely ambiguous hour in front of a site
 * operator working at speed.
 *
 * THE LIST IS A SHORTCUT, NOT THE VOCABULARY. Quarter hours cover the shift
 * starts, and the field above the list takes anything else — 07:07 for a
 * late-arrival threshold, 12:38 for a break window. A picker that could only
 * express multiples of fifteen would send the reader back to a text box
 * somewhere else in the same form, which is how a product ends up with two
 * controls for one field.
 *
 * NO CLOCK IS READ. A time here is a wall-clock reading with no date and no
 * zone; nothing constructs a `Date`, so there is nothing to shift.
 * ======================================================================== */

export interface TimeFieldProps {
  /** `HH:MM`, 24-hour, or '' for nothing chosen. */
  value: string;
  onChange: (value: TimeValue) => void;
  /** Minutes between the offered steps. 15 by default. */
  stepMinutes?: number;
  disabled?: boolean;
  /** Announced on the trigger as `aria-required`. See `DateTrigger`. */
  required?: boolean;
  invalid?: boolean;
  id?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
  labels?: Partial<DateFieldLabels>;
  className?: string;
}

export function TimeField({
  value,
  onChange,
  stepMinutes = 15,
  disabled,
      required,
  invalid,
  id,
  labels: labelOverride,
  className,
  ...aria
}: TimeFieldProps): JSX.Element {
  const labels = useDateFieldLabels(labelOverride);
  const panelId = useId();
  const inputId = useId();
  const errorId = `${inputId}-error`;
  const anchorRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  /* THE CARET GOES BACK, AND IT GOES BACK AFTER THE PANEL HAS GONE. Focusing
     the trigger while the focus trap is still mounted makes the trap pull focus
     straight back into a panel that is about to unmount, and the reader lands
     on `<body>` with no way back to the control they just used. Same ref-and-
     effect round trip `FiltersPopover` uses, for the same reason. */
  const restoreFocus = useRef(false);
  const listRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(value);
  const [error, setError] = useState<string | null>(null);
  const steps = quarterHourSteps(stepMinutes);

  useEffect(() => {
    setText(value);
    setError(null);
  }, [value]);

  const dismiss = useCallback(() => {
    restoreFocus.current = true;
    setOpen(false);
  }, []);

  useEffect(() => {
    if (open || !restoreFocus.current) return;
    restoreFocus.current = false;
    triggerRef.current?.focus();
  }, [open]);

  /* The list opens SHOWING where the reader already is. A 96-row list scrolled
     to 00:00 while the field reads 17:30 is a list refusing to answer the
     question it was opened to answer. */
  useEffect(() => {
    if (!open) return;
    const anchor = nearestStepAtOrBelow(value, steps);
    if (!anchor) return;
    const el = listRef.current?.querySelector(`[data-step="${anchor}"]`);
    /* Guarded, like the `ResizeObserver` in `useAnchoredPanel`: this is a
       library, and an environment without `scrollIntoView` (jsdom, where every
       test of this component runs) must lose the scroll, never throw. The list
       then opens at the top — a degradation, not a crash. */
    if (el instanceof HTMLElement && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'center' });
    }
  }, [open, value, steps]);

  function commit(): void {
    if (!text.trim()) {
      setError(null);
      onChange('');
      return;
    }
    const parsed = parseTime(text);
    if (!parsed) {
      setError(labels.time.unreadable);
      return;
    }
    setError(null);
    onChange(parsed);
  }

  return (
    <div ref={anchorRef} className={cn('relative', className)}>
      <DateTrigger
        required={required}
        ref={triggerRef}
        id={id}
        text={isTimeValue(value) ? value : ''}
        placeholder={labels.time.placeholder}
        open={open}
        panelId={panelId}
        disabled={disabled}
        invalid={invalid}
        glyph="clock"
        ariaLabel={
          aria['aria-label'] ?? (aria['aria-labelledby'] ? undefined : labels.time.triggerAria)
        }
        ariaLabelledBy={aria['aria-labelledby']}
        ariaDescribedBy={aria['aria-describedby']}
        onToggle={() => (open ? dismiss() : setOpen(true))}
      />

      <DatePopover
        open={open}
        onDismiss={dismiss}
        triggerRef={triggerRef}
        anchorRef={anchorRef}
        id={panelId}
        ariaLabel={aria['aria-label'] ?? labels.time.triggerAria}
      >
        <div className="flex flex-col gap-element">
          <div className="space-y-tight">
            <Label htmlFor={inputId} className="text-label text-muted-foreground">
              {labels.time.placeholder}
            </Label>
            <Input
              id={inputId}
              type="text"
              inputMode="numeric"
              autoComplete="off"
              autoFocus
              value={text}
              placeholder={labels.time.placeholder}
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? errorId : undefined}
              onChange={(e) => {
                setText(e.target.value);
                setError(null);
              }}
              onBlur={commit}
              onKeyDown={(e) => {
                if (e.key !== 'Enter') return;
                e.preventDefault();
                commit();
              }}
              // `dir="ltr"` on the box itself: a clock reading is a Latin run
              // whose colon is a NEUTRAL character, and inside an Arabic
              // paragraph the bidi algorithm places the two digit groups
              // right-to-left — the reader sees 30:07. Same defect, and the
              // same fix, as the `.font-mono` rule in the Tailwind preset.
              dir="ltr"
            />
            {error && (
              <p id={errorId} role="alert" className="text-copy-sm text-danger-on-surface">
                {error}
              </p>
            )}
          </div>

          <div
            ref={listRef}
            role="listbox"
            aria-label={labels.time.listAria}
            // A capped, scrolling list. The popover shell already caps its own
            // height against the viewport; this is the shape the list wants on
            // a tall screen, where 96 rows would otherwise fill the glass.
            className="max-h-56 overflow-y-auto"
          >
            {steps.map((step) => {
              const selected = step === value;
              return (
                <button
                  key={step}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  data-step={step}
                  dir="ltr"
                  onClick={() => {
                    onChange(step);
                    dismiss();
                  }}
                  className={cn(
                    // `text-figure`, not `text-copy`: every row in this list
                    // is a clock time, and a column of times has to line up.
                    // The step carries Archivo + `tabular-nums` — see
                    // ../../tailwind-type.ts.
                    'flex w-full items-center rounded-xs px-3 py-1.5 text-figure',
                    'transition-colors duration-fast ease-standard',
                    selected
                      ? 'bg-primary text-primary-foreground'
                      : 'text-foreground hover:bg-muted',
                    focusRing
                  )}
                >
                  {step}
                </button>
              );
            })}
          </div>
        </div>
      </DatePopover>
    </div>
  );
}
